/**
 * Header / footer settings (module 22 editor parity).
 *
 * The storefront's chrome (logo text, nav, header toggles, footer copy) is
 * per-store appearance, so it lives on the theme and stages/publishes with
 * everything else. Edited from the fixed "Header" and "Footer" entries in the
 * section list.
 */
import { Plus, Trash2 } from "lucide-react";
import { Input, Textarea, Button } from "@flint/ui";
import type {
  StoreHeaderConfig,
  StoreFooterConfig,
  StoreHeaderNavItem,
  LinkTarget,
} from "@flint/api-contract/store-home";
import { LabelledField } from "./labelled-field.tsx";

function Toggle({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-ink">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

export function HeaderForm({
  value,
  canEdit,
  collections,
  pages,
  hasLogoImage,
  onChange,
}: {
  value: StoreHeaderConfig;
  canEdit: boolean;
  collections: Array<{ slug: string; name: string }>;
  /** Published + draft content pages a link can point at (module 22). */
  pages: Array<{ slug: string; title: string }>;
  /** A logo image (Theme → Branding) hides the wordmark text — say so here. */
  hasLogoImage?: boolean;
  onChange: (v: StoreHeaderConfig) => void;
}) {
  const set = <K extends keyof StoreHeaderConfig>(k: K, v: StoreHeaderConfig[K]) =>
    onChange({ ...value, [k]: v });
  const nav: StoreHeaderNavItem[] = value.nav ?? [];

  return (
    <div className="flex flex-col gap-4">
      <LabelledField
        label="Logo text"
        hint={<span className="text-xs text-muted">{(value.logoText ?? "").length}/40</span>}
      >
        {(id) => (
          <Input
            id={id}
            value={value.logoText ?? ""}
            maxLength={40}
            disabled={!canEdit}
            placeholder={hasLogoImage ? "Optional — shown beside your logo" : "Defaults to your store name"}
            onChange={(e) => set("logoText", e.target.value || undefined)}
          />
        )}
      </LabelledField>
      <p className="-mt-2 text-xs text-muted">
        {hasLogoImage
          ? "Shown beside your logo image (icon + wordmark). Leave blank to show just the logo."
          : "Your header wordmark. Upload a logo in Theme → Branding to show an icon beside it."}
      </p>

      {/* Custom nav — a label + where it points (any target). */}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-ink">Navigation links</span>
        {nav.length === 0 ? (
          <p className="text-xs text-muted">
            {value.autoCollectionNav ?? true
              ? "Empty — the header lists every collection automatically. Add links to curate the menu, or turn the auto-list off below."
              : "Empty — the header shows no menu. Add links to build one, or turn the auto-list back on below."}
          </p>
        ) : null}
        {nav.map((item, i) => {
          const patch = (next: StoreHeaderNavItem) =>
            set("nav", nav.map((n, j) => (j === i ? next : n)));
          return (
            <div key={i} className="flex flex-col gap-2 rounded-sm border border-line p-2">
              <div className="flex items-center gap-2">
                <Input
                  value={item.label}
                  maxLength={24}
                  disabled={!canEdit}
                  placeholder="Label"
                  onChange={(e) => patch({ ...item, label: e.target.value })}
                />
                <button
                  type="button"
                  disabled={!canEdit}
                  aria-label={`Remove link ${i + 1}`}
                  onClick={() => set("nav", nav.filter((_, j) => j !== i))}
                  className="rounded-sm p-1 text-muted hover:text-bad"
                >
                  <Trash2 size={14} aria-hidden />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="h-9 shrink-0 rounded-sm border border-line bg-paper px-2 text-sm"
                  disabled={!canEdit}
                  value={item.target.kind}
                  onChange={(e) => {
                    const k = e.target.value;
                    const target: LinkTarget =
                      k === "collection"
                        ? { kind: "collection", slug: collections[0]?.slug ?? "" }
                        : k === "page"
                          ? { kind: "page", slug: pages[0]?.slug ?? "" }
                          : k === "url"
                            ? { kind: "url", href: "https://" }
                            : k === "whatsapp"
                              ? { kind: "whatsapp" }
                              : { kind: "search" };
                    patch({ ...item, target });
                  }}
                >
                  <option value="search">Search / all products</option>
                  <option value="collection" disabled={collections.length === 0}>
                    A collection
                  </option>
                  <option value="page" disabled={pages.length === 0}>
                    A page
                  </option>
                  <option value="url">External link</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
                {item.target.kind === "collection" ? (
                  <select
                    className="h-9 flex-1 rounded-sm border border-line bg-paper px-2 text-sm"
                    disabled={!canEdit}
                    value={item.target.slug}
                    onChange={(e) => patch({ ...item, target: { kind: "collection", slug: e.target.value } })}
                  >
                    {collections.map((col) => (
                      <option key={col.slug} value={col.slug}>
                        {col.name}
                      </option>
                    ))}
                  </select>
                ) : item.target.kind === "page" ? (
                  <select
                    className="h-9 flex-1 rounded-sm border border-line bg-paper px-2 text-sm"
                    disabled={!canEdit}
                    value={item.target.slug}
                    onChange={(e) => patch({ ...item, target: { kind: "page", slug: e.target.value } })}
                  >
                    {pages.map((pg) => (
                      <option key={pg.slug} value={pg.slug}>
                        {pg.title}
                      </option>
                    ))}
                  </select>
                ) : item.target.kind === "url" ? (
                  <Input
                    value={item.target.href}
                    disabled={!canEdit}
                    placeholder="https://"
                    onChange={(e) => patch({ ...item, target: { kind: "url", href: e.target.value } })}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
        <Button
          type="button"
          intent="ghost"
          size="md"
          disabled={!canEdit || nav.length >= 6}
          onClick={() => set("nav", [...nav, { label: "New link", target: { kind: "search" } }])}
        >
          <Plus size={14} aria-hidden />
          Add link
        </Button>
        {nav.length === 0 ? (
          <Toggle
            label="List my collections automatically"
            checked={value.autoCollectionNav ?? true}
            disabled={!canEdit}
            onChange={(v) => set("autoCollectionNav", v)}
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-3 border-t border-line pt-3">
        <Toggle label="Show search" checked={value.showSearch} disabled={!canEdit} onChange={(v) => set("showSearch", v)} />
        <Toggle label="Show cart" checked={value.showCart} disabled={!canEdit} onChange={(v) => set("showCart", v)} />
        <Toggle label="Show account / sign-in" checked={value.showAccount} disabled={!canEdit} onChange={(v) => set("showAccount", v)} />
        <Toggle label="Stick to the top on scroll" checked={value.sticky} disabled={!canEdit} onChange={(v) => set("sticky", v)} />
      </div>
    </div>
  );
}

export function FooterForm({
  value,
  canEdit,
  onChange,
}: {
  value: StoreFooterConfig;
  canEdit: boolean;
  onChange: (v: StoreFooterConfig) => void;
}) {
  const set = <K extends keyof StoreFooterConfig>(k: K, v: StoreFooterConfig[K]) =>
    onChange({ ...value, [k]: v });
  return (
    <div className="flex flex-col gap-4">
      <LabelledField
        label="About blurb"
        hint={<span className="text-xs text-muted">{(value.about ?? "").length}/300</span>}
      >
        {(id) => (
          <Textarea
            id={id}
            value={value.about ?? ""}
            maxLength={300}
            rows={3}
            disabled={!canEdit}
            placeholder="A line or two about your store, shown in the footer."
            onChange={(e) => set("about", e.target.value || undefined)}
          />
        )}
      </LabelledField>
      <LabelledField
        label="Bottom line"
        hint={<span className="text-xs text-muted">{(value.tagline ?? "").length}/120</span>}
      >
        {(id) => (
          <Input
            id={id}
            value={value.tagline ?? ""}
            maxLength={120}
            disabled={!canEdit}
            placeholder="GST-ready · INR · Wholesale packs"
            onChange={(e) => set("tagline", e.target.value || undefined)}
          />
        )}
      </LabelledField>
      <div className="flex flex-col gap-3 border-t border-line pt-3">
        <Toggle label="Show contact column" checked={value.showContact} disabled={!canEdit} onChange={(v) => set("showContact", v)} />
        <Toggle label="Dark footer" checked={value.dark} disabled={!canEdit} onChange={(v) => set("dark", v)} />
      </div>
      <p className="text-xs text-muted">
        “Powered by Flint”, collection links and copyright are added automatically.
      </p>
    </div>
  );
}
